import {
  MINIMAL_STATIONS_COUNT_FOR_DURATION_CALCULATION,
  SECONDS_IN_MINUTE,
  ZERO_TIME_HHmm,
} from "../../constants";
import { TimeZoneEnum } from "../../enums";
import {
  ICourseDto,
  ICourseMetadata,
  ICourseStationInfo,
  TTimeHHmm,
} from "../../models";
import {
  buildCumulativeTimeArrivalsFromLegSeconds,
  getDayjsFromTimeString,
  isValidTimeString,
  parseHHmmTimeToMinutes,
  resolveAppliedLegSeconds,
} from "../date.helpers";
import { Log } from "../log.helpers";
import { hasKeys } from "../objects.helpers";
import { getStationAreasInfo } from "../ride/ride-station-areas.helpers";
import { ensureRoutingWaypointsAreInterior } from "../routing-waypoint";

interface INormalizeCourseMetadataParams {
  metadata: ICourseMetadata | undefined;
  reorderStationsByTime?: boolean;
  timezone: TimeZoneEnum;
}

export const normalizeCourseMetadata = ({
  metadata,
  reorderStationsByTime = false,
  timezone,
}: INormalizeCourseMetadataParams): ICourseMetadata => {
  if (!metadata?.stationsInfo?.length) {
    return metadata || {};
  }

  const sortedStations = ensureRoutingWaypointsAreInterior(
    reorderStationsByTime
      ? sortCourseStationsByTime(metadata.stationsInfo, timezone)
      : metadata.stationsInfo,
  );

  const { stationIds, areaIds, innerAreaIds } =
    getStationAreasInfo(sortedStations);
  return {
    ...metadata,
    stationsInfo: sortedStations,
    stationIds,
    areaIds,
    innerAreaIds,
  };
};

export const reorderCourseStationsByOrderedArray = (
  metadata: ICourseMetadata | undefined,
  stationNewOrderIds: number[],
  timezone: TimeZoneEnum,
): ICourseMetadata | undefined => {
  if (!metadata?.stationsInfo?.length || !stationNewOrderIds?.length) {
    return metadata;
  }

  const stationInfos = metadata.stationsInfo;

  const stationInfosMap = new Map(stationInfos.map(s => [s.id, s]));
  const stationsInfoFiltered = stationNewOrderIds
    .map(x => stationInfosMap.get(x))
    .filter(x => x !== undefined);

  const stationIdsFiltered = stationsInfoFiltered.map(x => x?.id || 0);
  const metadataUpdated: ICourseMetadata = {
    ...metadata,
    stationIds: stationIdsFiltered,
    stationsInfo: stationsInfoFiltered,
  };
  const metadataTransformed = normalizeCourseMetadata({
    metadata: metadataUpdated,
    reorderStationsByTime: false,
    timezone,
  });
  return metadataTransformed;
};

interface IInheritPredecessorTimeForStationParams {
  stationsInfo: ICourseStationInfo[];
  stationId: number;
}

/**
 * Manual-ordering support (doNotAutoUpdateCourses ON): make a dragged station keep the position it
 * was dropped into by giving it the timeArrival of the station now placed before it. The backend
 * persists stations sorted by time with a stable sort, so tying the moved station's time to its
 * predecessor keeps it adjacent to that predecessor and preserves the manual order. A station moved
 * to the top has no predecessor, so it falls back to ZERO_TIME_HHmm ("00:00").
 */
export const inheritPredecessorTimeForStation = ({
  stationsInfo,
  stationId,
}: IInheritPredecessorTimeForStationParams): ICourseStationInfo[] => {
  const stationIndex = stationsInfo.findIndex(
    station => station.id === stationId,
  );

  if (stationIndex < 0) {
    return stationsInfo;
  }

  const predecessorTime =
    stationIndex > 0
      ? stationsInfo[stationIndex - 1].timeArrival
      : ZERO_TIME_HHmm;

  return stationsInfo.map((station, index) =>
    index === stationIndex
      ? { ...station, timeArrival: predecessorTime || ZERO_TIME_HHmm }
      : station,
  );
};

interface IInheritLegMetricsByStationParams {
  previousStationsInfo: ICourseStationInfo[];
  newStationsInfo: ICourseStationInfo[];
  distances?: number[];
  durations?: number[];
}

interface ICourseLegMetrics {
  distances: number[];
  durations: number[];
  totalDistance: number;
  totalDuration: number;
}

/**
 * Manual-ordering support (doNotAutoUpdateCourses ON): keep the per-leg route arrays consistent with a
 * manually added / removed / reordered station list WITHOUT a route recalc. Legs are keyed to the station
 * they arrive at (leg i connects station i -> i+1, so the station at index k >= 1 owns arrays[k - 1]) and
 * the arrays are rebuilt for the new order: a station keeps its own incoming leg, a brand-new station
 * inherits its predecessor's, a removed station's leg drops out, and a reorder carries each station's leg
 * with it. The result is always length `stations - 1`, which course production requires
 * (validateBuildFixedRideParams: stationsInfo.length === durations.length + 1). Totals are the sum of legs.
 */
export const inheritLegMetricsByStation = ({
  previousStationsInfo,
  newStationsInfo,
  distances,
  durations,
}: IInheritLegMetricsByStationParams): ICourseLegMetrics => {
  const legByStationId = new Map<
    number,
    { distance: number; duration: number }
  >();
  for (let index = 1; index < previousStationsInfo.length; index++) {
    const stationId = previousStationsInfo[index]?.id;
    if (stationId === undefined || stationId === null) {
      continue;
    }
    legByStationId.set(stationId, {
      distance: distances?.[index - 1] ?? 0,
      duration: durations?.[index - 1] ?? 0,
    });
  }

  const newDistances: number[] = [];
  const newDurations: number[] = [];
  for (let index = 1; index < newStationsInfo.length; index++) {
    const stationId = newStationsInfo[index]?.id;
    const predecessorId = newStationsInfo[index - 1]?.id;
    const ownLeg =
      stationId !== undefined && stationId !== null
        ? legByStationId.get(stationId)
        : undefined;
    const predecessorLeg =
      predecessorId !== undefined && predecessorId !== null
        ? legByStationId.get(predecessorId)
        : undefined;
    const leg = ownLeg ?? predecessorLeg ?? { distance: 0, duration: 0 };
    newDistances.push(leg.distance);
    newDurations.push(leg.duration);
  }

  const legMetrics: ICourseLegMetrics = {
    distances: newDistances,
    durations: newDurations,
    totalDistance: newDistances.reduce(
      (sum, distance) => sum + (distance || 0),
      0,
    ),
    totalDuration: newDurations.reduce(
      (sum, duration) => sum + (duration || 0),
      0,
    ),
  };
  return legMetrics;
};

interface IInheritLegMetricsForManualCourseChangeParams {
  previousStationsInfo: ICourseStationInfo[];
  metadata: ICourseMetadata;
}

/**
 * Apply {@link inheritLegMetricsByStation} to a course metadata after a manual station change, returning
 * metadata with consistent distances/durations/totals. originStationId/destinationStationId and
 * routePolyline are intentionally left untouched (manual mode does not recompute route geometry);
 * stationIds/areaIds are already refreshed by normalizeCourseMetadata.
 */
export const inheritLegMetricsForManualCourseChange = ({
  previousStationsInfo,
  metadata,
}: IInheritLegMetricsForManualCourseChangeParams): ICourseMetadata => {
  const { distances, durations, totalDistance, totalDuration } =
    inheritLegMetricsByStation({
      previousStationsInfo,
      newStationsInfo: metadata.stationsInfo || [],
      distances: metadata.distances,
      durations: metadata.durations,
    });

  return { ...metadata, distances, durations, totalDistance, totalDuration };
};

export const sortCourseStationsByTime = (
  stations: ICourseStationInfo[],
  timezone: TimeZoneEnum,
): ICourseStationInfo[] => {
  return [...stations].sort((a, b) => {
    if (
      !a.timeArrival ||
      !b.timeArrival ||
      !isValidTimeString(a.timeArrival) ||
      !isValidTimeString(b.timeArrival)
    ) {
      return 0;
    }

    const date1 = getDayjsFromTimeString(a.timeArrival, timezone).valueOf();
    const date2 = getDayjsFromTimeString(b.timeArrival, timezone).valueOf();

    return date1 - date2;
  });
};

interface IRemoveStationFromCourseDtoMetadataParams {
  courseDto: ICourseDto;
  stationsToRemoveIds: number[];
  timezone: TimeZoneEnum;
}

export const removeStationFromCourseDtoMetadata = ({
  courseDto,
  stationsToRemoveIds,
  timezone,
}: IRemoveStationFromCourseDtoMetadataParams): ICourseMetadata => {
  const { metadata } = courseDto;

  if (!metadata || !hasKeys(metadata) || !stationsToRemoveIds?.length) {
    return {};
  }

  const stationsToRemoveIdsSet = new Set(stationsToRemoveIds);
  const metadataUpdated: ICourseMetadata = {
    ...metadata,
    stationIds:
      metadata.stationIds?.filter(x => !stationsToRemoveIdsSet.has(x)) || [],
    stationsInfo:
      metadata.stationsInfo?.filter(x => !stationsToRemoveIdsSet.has(x.id)) ||
      [],
  };

  const metadataTransformed = normalizeCourseMetadata({
    metadata: metadataUpdated,
    reorderStationsByTime: true,
    timezone,
  });
  return metadataTransformed;
};

export const updateCourseMetadataStationsTime = (
  courseDto: ICourseDto,
  editStationId: number,
  timeStringHHmm: TTimeHHmm | "",
): ICourseMetadata => {
  const stations = [...(courseDto?.metadata?.stationsInfo || [])];
  const stationChangeFromIndex = stations.findIndex(
    x => x.id === editStationId,
  );
  const stationsUpdated: ICourseStationInfo[] = stations.map(
    (station, index) => {
      if (index !== stationChangeFromIndex) {
        return {
          ...station,
          timeArrival: station.timeArrival || ZERO_TIME_HHmm,
        };
      }

      return { ...station, timeArrival: timeStringHHmm || ZERO_TIME_HHmm };
    },
  );

  const metadataToUpdate: ICourseMetadata = {
    ...(courseDto.metadata || {}),
    stationsInfo: stationsUpdated,
  };
  Log.debug(
    `Ride metadata after time update from index ${stationChangeFromIndex} by ${timeStringHHmm} min.:`,
    metadataToUpdate,
  );
  return metadataToUpdate;
};

/**
 * Seconds each leg (leg i connects station i -> i + 1) currently spans on the live HH:mm timeline,
 * `undefined` where a station has no usable arrival time. This is the timeline the editor shows, so it
 * is what "the current plan" means — including any time the user just typed and has not saved yet.
 */
const getCourseCurrentLegSeconds = (
  stations: ICourseStationInfo[],
): (number | undefined)[] =>
  stations.slice(1).map((station, index) => {
    const previousTime = stations[index]?.timeArrival;
    const nextTime = station.timeArrival;

    if (
      !previousTime ||
      !nextTime ||
      !isValidTimeString(previousTime) ||
      !isValidTimeString(nextTime)
    ) {
      return undefined;
    }

    const diffMinutes =
      parseHHmmTimeToMinutes(nextTime) - parseHHmmTimeToMinutes(previousTime);
    return Math.max(diffMinutes, 0) * SECONDS_IN_MINUTE;
  });

/**
 * Planned seconds per leg for the measured-times preview, `undefined` where the plan is unknown.
 * The HH:mm timeline is the source of truth because the editor writes it directly; `metadata.durations`
 * is only trusted where it still agrees with that timeline to the minute, which is the resolution a
 * course is stored at. A stale durations entry — the routine outcome of a hand-edited station time,
 * since the single-station writers never refresh the array — is ignored rather than shown as the plan.
 */
export const getCoursePlannedLegSeconds = (
  metadata: ICourseMetadata | undefined,
): (number | undefined)[] => {
  const stations = metadata?.stationsInfo || [];

  if (stations.length < MINIMAL_STATIONS_COUNT_FOR_DURATION_CALCULATION) {
    return [];
  }

  const currentLegSeconds = getCourseCurrentLegSeconds(stations);
  const { durations } = metadata || {};

  if (durations?.length !== currentLegSeconds.length) {
    return currentLegSeconds;
  }

  return currentLegSeconds.map((currentSeconds, legIndex) => {
    const duration = Math.max(durations[legIndex] || 0, 0);
    const isDurationInSyncWithTimeline =
      currentSeconds !== undefined &&
      Math.abs(duration - currentSeconds) < SECONDS_IN_MINUTE;

    return isDurationInSyncWithTimeline ? duration : currentSeconds;
  });
};

interface IUpdateCourseMetadataStationsTimeBulkParams {
  courseDto: Partial<ICourseDto>;
  /** One entry per leg; `undefined` means "the user picked nothing here, keep the current delta". */
  legSeconds: (number | undefined)[];
}

/**
 * Bulk sibling of {@link updateCourseMetadataStationsTime}: rewrites the arrival time of EVERY station
 * from a per-leg seconds array instead of moving a single one. The first station is the anchor and
 * keeps the time it already had, so a course that does not start at 00:00 is not silently rebased.
 * Legs the user did not pick keep the delta they currently have rather than being rebuilt from a stale
 * plan, and stations beyond the end of `legSeconds` keep the time they already had.
 */
export const updateCourseMetadataStationsTimeBulk = ({
  courseDto,
  legSeconds,
}: IUpdateCourseMetadataStationsTimeBulkParams): ICourseMetadata => {
  const stations = courseDto?.metadata?.stationsInfo || [];
  const anchorTime = stations.at(0)?.timeArrival || ZERO_TIME_HHmm;
  const timeArrivals = buildCumulativeTimeArrivalsFromLegSeconds(
    resolveAppliedLegSeconds({
      appliedLegSeconds: legSeconds,
      currentLegSeconds: getCourseCurrentLegSeconds(stations),
    }),
    anchorTime,
  );

  const stationsUpdated: ICourseStationInfo[] = stations.map(
    (station, index) => ({
      ...station,
      timeArrival: timeArrivals[index] ?? station.timeArrival,
    }),
  );

  const metadataToUpdate: ICourseMetadata = {
    ...(courseDto.metadata || {}),
    stationsInfo: stationsUpdated,
  };
  Log.debug(
    "Course metadata after bulk station time update:",
    metadataToUpdate,
  );
  return metadataToUpdate;
};
